import { flatten } from "lodash";
import type {SimpleMenuItem} from "@octopusdeploy/design-system-components";
import {isUrlActive} from "../Navigation/isUrlActive";

export interface MenuItem {
    url: string;
    text: string;
    title?: string;
    icon?: JSX.Element;
    exact?: boolean;
}

export interface MenuGroup {
    label: string;
    children: MenuNode[];
}

export interface MenuPopper {
    icon: JSX.Element;
    url: string;
    exact?: boolean;
    text?: string;
    title?: string;
    onClick: (e: HTMLElement) => void;
}

export type MenuNode = MenuGroup | MenuItem | MenuPopper;

export function isMenuGroup(node: MenuNode): node is MenuGroup {
    return (node as MenuGroup).children !== undefined;
}

export function isMenuPopper(node: MenuNode): node is MenuPopper {
    return (node as MenuPopper).onClick !== undefined;
}

export function hasActiveDescendants(pathname: string, spaceId: string, group: MenuGroup): boolean {
    const allDescendantLinks = (_: MenuGroup): Array<MenuItem | MenuPopper> => flatten(_.children.map((c: MenuNode) => {
        if (isMenuGroup(c)) {
            return allDescendantLinks(c);
        }
        return [c];
    }));

    return allDescendantLinks(group).some(l => isUrlActive(pathname, spaceId, l.url, l.exact));
}

export function menuNodesToSimpleMenuItems(nodes: MenuNode[], onClose?: () => void): SimpleMenuItem[] {
    return nodes.map(node => {
        if (isMenuGroup(node)) {
            return {
                type: "nested-menu",
                label: node.label,
                children: menuNodesToSimpleMenuItems(node.children, onClose),
            };
        }
        if (isMenuPopper(node)) {
            return {
                type: "button",
                label: node.text || node.title || "",
                onClick: () => {
                    onClose?.();
                    node.onClick(document.activeElement as HTMLElement);
                },
            };
        }
        return {
            type: "internal-link",
            label: node.text,
            path: node.url as any,
            onClick: onClose,
        };
    });
}
